import vuetify from "@/plugins/vuetify";
import { ThemeModule } from "@/store/modules/ThemeVuexModule";
import {
  COLOR_ACCENT_DARK,
  COLOR_DARK_FILL_TEXT,
  COLOR_NOT_SELECTED_QUALITY_RATING,
  COLOR_NOT_SELECTED_QUALITY_RATING_DARK,
  COLOR_STANDARD_WHITE,
  DarkFillRedRole,
  darkFillFor,
} from "@/utils/colors";

/**
 * Picks a color based on the currently active theme.
 *
 * Call this from a component computed getter (or any reactive context) that binds a
 * color to `:color` / `:style`; because it reads the reactive `vuetify.framework.theme.dark`
 * flag, the getter re-evaluates when the theme is toggled. Use it to give hardcoded
 * light-mode colors (white backgrounds, light-grey headers) a dark-mode counterpart.
 *
 * @param light color to use in light mode.
 * @param dark color to use in dark mode.
 */
export function themed(light: string, dark: string): string {
  return vuetify.framework.theme.dark ? dark : light;
}

/**
 * The fill of a surface that carries text (button, chip, tile, badge), for the active theme.
 *
 * Light mode returns `light` untouched. Dark mode returns the one palette shade of that colour
 * (see "Dark-mode status palette" in `@/utils/colors`), so every yellow, green, … in the dark
 * theme is the same shade whichever tier the light theme used. A value that is not a palette fill
 * (a user-picked colour, a surface colour, `""`) passes through unchanged.
 *
 * Call it where the colour is bound (template or computed getter), not where it is produced, so
 * code that compares colours by value keeps seeing the light constants. Pair it with
 * {@link themedFillTextColor} or {@link themedFillTextClass} for the label.
 *
 * @param light the light-mode fill (hex or Vuetify colour name).
 * @param redRole for the brand red `#e6323c` only: `"action"` (default) keeps the brand red,
 *   `"status"` gives the muted status red.
 */
export function themedFill(light: string, redRole: DarkFillRedRole = "action"): string {
  if (!vuetify.framework.theme.dark) {
    return light;
  }

  return darkFillFor(light, redRole) ?? light;
}

/**
 * The label colour for a surface filled by {@link themedFill}.
 *
 * Light mode returns `lightText`. In dark mode a palette fill gets dark text (white on the action
 * red); an unmapped fill keeps `lightText`, since its fill did not change either.
 */
export function themedFillTextColor(
  light: string,
  lightText: string,
  redRole: DarkFillRedRole = "action"
): string {
  if (!vuetify.framework.theme.dark) {
    return lightText;
  }

  const dark = darkFillFor(light, redRole);

  if (!dark) {
    return lightText;
  }

  return dark === COLOR_ACCENT_DARK ? COLOR_STANDARD_WHITE : COLOR_DARK_FILL_TEXT;
}

/**
 * {@link themedFillTextColor} as a Vuetify text class, for buttons that set their label by class.
 *
 * In dark mode a palette fill gets `black--text` (`white--text` on the action red); otherwise
 * `lightClass` is returned.
 */
export function themedFillTextClass(
  light: string,
  lightClass: string,
  redRole: DarkFillRedRole = "action"
): string {
  if (!vuetify.framework.theme.dark) {
    return lightClass;
  }

  const dark = darkFillFor(light, redRole);

  if (!dark) {
    return lightClass;
  }

  return dark === COLOR_ACCENT_DARK ? "white--text" : "black--text";
}

/**
 * A cell of a toggling rating row (see ui-conventions "Quality rating scales") for the active theme.
 *
 * The selected cell takes the palette yellow with a dark label. An unselected cell is the raised
 * dark surface in dark mode ({@link COLOR_NOT_SELECTED_QUALITY_RATING_DARK}, as in the RENO rating),
 * keeping the theme's white label, because a palette grey would read as a second "chosen" state.
 * Light mode returns `lightColor` with no label class.
 */
export function themedRatingCell(lightColor: string): { color: string; textClass: string } {
  if (!vuetify.framework.theme.dark) {
    return { color: lightColor, textClass: "" };
  }

  if (lightColor === COLOR_NOT_SELECTED_QUALITY_RATING) {
    return { color: COLOR_NOT_SELECTED_QUALITY_RATING_DARK, textClass: "" };
  }

  return { color: themedFill(lightColor), textClass: themedFillTextClass(lightColor, "") };
}

/** HSL lightness floor applied to chart series colors in dark mode. */
const DARK_CHART_SERIES_MIN_LIGHTNESS = 0.55;

/**
 * Adapts a chart series (line) color for the active theme.
 *
 * The drying-graph palette was designed for a white plot background; on the dark
 * plot backdrop the dark-toned lines (navy/dark red/midnight blues) lose too much
 * contrast. In dark mode this lifts the HSL lightness to a floor of
 * {@link DARK_CHART_SERIES_MIN_LIGHTNESS} while keeping hue/saturation (and any
 * `#rrggbbaa` alpha suffix) intact, so each series stays recognizably "its" color.
 * In light mode the color is returned unchanged. Non-hex inputs pass through.
 *
 * NOTE: this is evaluated when datasets/legend metadata are built (extraction
 * time), so both the graph lines and the legend colors stay identical strings —
 * which the legend-hover highlight matching relies on.
 */
export function chartSeriesColor(color: string): string {
  if (!vuetify.framework.theme.dark) {
    return color;
  }

  const match = /^#([0-9a-fA-F]{6})([0-9a-fA-F]{2})?$/.exec(color);
  if (!match) {
    return color;
  }
  const alpha = match[2] ?? "";

  const r = parseInt(match[1].slice(0, 2), 16) / 255;
  const g = parseInt(match[1].slice(2, 4), 16) / 255;
  const b = parseInt(match[1].slice(4, 6), 16) / 255;

  // RGB -> HSL
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  const newL = Math.max(l, DARK_CHART_SERIES_MIN_LIGHTNESS);
  if (newL === l) {
    return color;
  }

  // HSL -> RGB
  const c = (1 - Math.abs(2 * newL - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = newL - c / 2;
  let r1 = 0, g1 = 0, b1 = 0;
  if (h < 60) [r1, g1, b1] = [c, x, 0];
  else if (h < 120) [r1, g1, b1] = [x, c, 0];
  else if (h < 180) [r1, g1, b1] = [0, c, x];
  else if (h < 240) [r1, g1, b1] = [0, x, c];
  else if (h < 300) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];

  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}${alpha}`;
}

/**
 * Switches the app between light and dark appearance and persists the choice.
 *
 * Mirrors `changeLanguageTo` in `@/i18n/Languages`: it updates the live Vuetify
 * runtime theme (`vuetify.framework.theme.dark`) and persists the preference via
 * {@link ThemeModule} (localStorage). Components should call this rather than
 * setting `$vuetify.theme.dark` directly, so the preference is always saved.
 *
 * @param isDark `true` for dark mode, `false` for light mode.
 */
export function changeThemeTo(isDark: boolean) {
  if (vuetify.framework.theme.dark !== isDark) {
    vuetify.framework.theme.dark = isDark;
  }
  ThemeModule.setDark(isDark);
}
